import { isCashAlert, isDaysAlert, isOnline, isOpenToday } from './pinCard';

// DJB-242: the search + filter rules for Accounts › Maps. Pure data helpers
// (no React, no Leaflet) so every combination is unit-testable; MapFilterBar.js
// renders the controls and AccountMaps.js applies the result to the pins.
//
// The filter state is deliberately "what is switched OFF": provinces are
// stored as a hidden set, so the default — everything visible — holds before
// the data is in and for provinces that only appear later.
//
// DJB-245: `onlineOnly` keeps just the machines the pin card shows with the
// green dot (pinCard.isOnline — last seen within the online window).
// DJB-257: `openOnly` keeps just the locations that are open today
// (todayStatus=open from the map data; closed / unknown drop out).

// machines without a province are grouped under this label (last in the row)
export const UNKNOWN_PROVINCE = 'Onbekend';

export const DEFAULT_FILTERS = Object.freeze({
  query: '',
  hiddenProvinces: Object.freeze([]),
  cashAlertOnly: false,
  daysAlertOnly: false,
  onlineOnly: false,
  openOnly: false,
});

export const provinceOf = (machine) => {
  const value = machine && machine.province != null ? String(machine.province).trim() : '';
  return value || UNKNOWN_PROVINCE;
};

// DJB-246: display label for a province chip. The backend stores provinces
// in capitals ("OOST-VLAANDEREN"); the chip shows normal capitalisation
// ("Oost-Vlaanderen"). Mixed-case values and short codes ("TBD") are kept
// as they are, so only the all-caps names get touched.
export const formatProvinceLabel = (province) => {
  const value = String(province == null ? '' : province).trim();
  if (!value || value.length <= 3 || value !== value.toUpperCase() || value === value.toLowerCase()) {
    return value;
  }
  return value
    .toLowerCase()
    .replace(/(^|[\s\-'’(/])(\p{L})/gu, (match, lead, letter) => lead + letter.toUpperCase());
};

// Distinct provinces of the given machines, alphabetically (locale-aware for
// the accented names), with "Onbekend" always last.
export const listProvinces = (machines) => {
  const set = new Set((Array.isArray(machines) ? machines : []).map(provinceOf));
  const hasUnknown = set.delete(UNKNOWN_PROVINCE);
  const sorted = Array.from(set).sort((a, b) => a.localeCompare(b, 'nl', { sensitivity: 'base' }));
  return hasUnknown ? [...sorted, UNKNOWN_PROVINCE] : sorted;
};

// Case- and accent-insensitive comparison base ("Gent" / "GENT" / "gênt").
export const normalizeSearchText = (value) =>
  String(value == null ? '' : value)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const SEARCH_FIELDS = ['name', 'city', 'province'];

// Every whitespace-separated word of the query must occur somewhere in the
// machine's name, city or province ("gent cafe" finds "Café Gent").
export const matchesQuery = (machine, query) => {
  const words = normalizeSearchText(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  if (!machine) return false;
  const haystack = SEARCH_FIELDS.map((field) => normalizeSearchText(machine[field])).join(' ');
  return words.every((word) => haystack.includes(word));
};

export const matchesProvinceFilter = (machine, hiddenProvinces) =>
  !(hiddenProvinces || []).includes(provinceOf(machine));

// Same thresholds as the card colours (pinCard.js): cash > €150, days > 90.
export const matchesAlertFilters = (machine, { cashAlertOnly, daysAlertOnly }) => {
  if (cashAlertOnly && !isCashAlert(machine && machine.cash)) return false;
  if (daysAlertOnly && !isDaysAlert(machine && machine.daysSinceCashout)) return false;
  return true;
};

// Same online rule as the card's green / grey dot (pinCard.js).
export const matchesOnlineFilter = (machine, onlineOnly, now = new Date()) =>
  !onlineOnly || isOnline(machine && machine.lastSeen, now);

// DJB-257: server-side todayStatus (Europe/Brussels), not the openDays array
export const matchesOpenFilter = (machine, openOnly) => !openOnly || isOpenToday(machine);

export const matchesFilters = (machine, filters = DEFAULT_FILTERS, now = new Date()) =>
  matchesQuery(machine, filters.query) &&
  matchesProvinceFilter(machine, filters.hiddenProvinces) &&
  matchesAlertFilters(machine, filters) &&
  matchesOnlineFilter(machine, filters.onlineOnly, now) &&
  matchesOpenFilter(machine, filters.openOnly);

// All filters combine (AND): search + provinces + cash + days + online + open.
// `now` is taken once per pass so every machine is judged against the same
// clock.
export const applyFilters = (machines, filters = DEFAULT_FILTERS, now = new Date()) =>
  (Array.isArray(machines) ? machines : []).filter((machine) =>
    matchesFilters(machine, filters, now)
  );

// Per-province count of the machines that pass every *other* filter — what
// the province would put on the map when switched on. Drives the chip badges
// so the numbers follow the search / cash / days toggles live.
export const countPerProvince = (machines, filters = DEFAULT_FILTERS, now = new Date()) => {
  const counts = {};
  applyFilters(machines, { ...filters, hiddenProvinces: [] }, now).forEach((machine) => {
    const province = provinceOf(machine);
    counts[province] = (counts[province] || 0) + 1;
  });
  return counts;
};

export const hasActiveFilters = (filters) =>
  Boolean(
    filters &&
      (normalizeSearchText(filters.query) ||
        (filters.hiddenProvinces && filters.hiddenProvinces.length > 0) ||
        filters.cashAlertOnly ||
        filters.daysAlertOnly ||
        filters.onlineOnly ||
        filters.openOnly)
  );

export const toggleProvince = (filters, province) => {
  const hidden = filters.hiddenProvinces || [];
  return {
    ...filters,
    hiddenProvinces: hidden.includes(province)
      ? hidden.filter((p) => p !== province)
      : [...hidden, province],
  };
};
